import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { AnimatePresence, motion } from 'motion/react';

export default function DesktopTopBar() {
  const navigate = useNavigate();
  const { resolvedTheme } = useTheme();
  const { isAuthenticated, user } = useAuth();
  const isDark = resolvedTheme === 'dark';
  const [searchQuery, setSearchQuery] = useState('');

  const barBg = isDark ? 'bg-[#141416]/80' : 'bg-white/80';
  const inputBg = isDark ? 'bg-white/[0.04]' : 'bg-neutral-100';
  const inputBorder = isDark ? 'border-white/[0.05]' : 'border-neutral-200/50';
  const textPrimary = isDark ? 'text-[#F5F5F7]' : 'text-neutral-900';
  const textSecondary = isDark ? 'text-[#6E6E73]' : 'text-neutral-400';

  return (
    <header
      className={`workspace-topbar hidden lg:flex flex-shrink-0 items-center justify-between h-[76px] px-8 ${barBg} backdrop-blur-xl border-b ${isDark ? 'border-white/[0.04]' : 'border-neutral-200/50'} z-30 transition-colors duration-500`}
    >
      {/* Search */}
      <div className="flex-1 max-w-[480px]">
        <div className={`relative ${inputBg} border ${inputBorder} rounded-xl transition-colors duration-200`}>
          <div className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
            <i className={`ri-search-line text-sm ${textSecondary}`} />
          </div>
          <input
            type="text"
            placeholder="Search lessons, subjects, topics..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && searchQuery.trim()) {
                navigate(`/history?q=${encodeURIComponent(searchQuery.trim())}`);
              }
            }}
            className={`w-full bg-transparent pl-10 pr-4 py-2.5 text-[13px] ${textPrimary} placeholder:text-[12px] ${isDark ? 'placeholder:text-[#6E6E73]' : 'placeholder:text-neutral-400'} focus:outline-none`}
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              aria-label="Clear search"
              className={`absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center ${textSecondary}`}
            >
              <i className="ri-close-line text-sm" />
            </button>
          )}
        </div>
      </div>

      {/* Right side actions */}
      <div className="flex items-center gap-3 ml-6">
        <AnimatePresence mode="wait">
          {isAuthenticated ? (
            <motion.div
              key="auth-actions"
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 10 }}
              transition={{ duration: 0.2 }}
              className="flex items-center gap-3"
            >
              <button
                onClick={() => navigate('/scan/start')}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-[13px] font-semibold ${isDark ? 'bg-white text-black hover:bg-white/90' : 'bg-neutral-900 text-white hover:bg-neutral-800'} transition-colors cursor-pointer whitespace-nowrap`}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-upload-cloud-2-line text-sm" />
                </div>
                <span>Upload</span>
              </button>

              <button
                onClick={() => navigate('/profile?panel=notifications')}
                aria-label="Open notification settings"
                className={`w-9 h-9 rounded-xl flex items-center justify-center transition-colors cursor-pointer ${isDark ? 'bg-white/[0.04] hover:bg-white/[0.08] text-[#6E6E73]' : 'bg-neutral-100 hover:bg-neutral-200 text-neutral-500'}`}
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-notification-3-line text-base" />
                </div>
              </button>

              <button
                onClick={() => navigate('/profile')}
                className={`flex items-center gap-2.5 pl-1 pr-3 py-1 rounded-xl transition-colors cursor-pointer ${isDark ? 'hover:bg-white/[0.04]' : 'hover:bg-neutral-100'}`}
              >
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center overflow-hidden ${isDark ? 'bg-white/[0.06]' : 'bg-neutral-100'}`}>
                  <span className={`text-sm font-bold ${textPrimary}`}>
                    {(user?.user_metadata?.display_name || user?.email?.split('@')[0] || 'G').charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="text-left">
                  <p className={`text-[12px] font-semibold ${textPrimary} leading-none`}>
                    {user?.user_metadata?.display_name || user?.email?.split('@')[0] || 'Guest'}
                  </p>
                  <p className={`text-[10px] ${textSecondary} leading-none mt-0.5`}>Your study space</p>
                </div>
              </button>
            </motion.div>
          ) : (
            <motion.div
              key="guest-actions"
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 10 }}
              transition={{ duration: 0.2 }}
              className="flex items-center gap-2"
            >
              <button
                onClick={() => navigate('/auth/login')}
                className={`px-4 py-2 rounded-xl text-[13px] font-semibold ${isDark ? 'text-white hover:bg-white/[0.06]' : 'text-neutral-900 hover:bg-neutral-100'} transition-colors cursor-pointer whitespace-nowrap`}
              >
                Log In
              </button>
              <button
                onClick={() => navigate('/auth/signup')}
                className={`px-4 py-2 rounded-xl text-[13px] font-semibold ${isDark ? 'bg-white text-black hover:bg-white/90' : 'bg-neutral-900 text-white hover:bg-neutral-800'} transition-colors cursor-pointer whitespace-nowrap`}
              >
                Sign Up
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
}